import { toast } from "react-toastify";
import { commonHeader, generateEndpoint } from "~/config/apiConfig";
import { ErrorBoundary } from "~/errors/errorBoundary";
import type {
  CreateProject,
  InviteProjectBody,
  Project,
  ProjectMember,
  Projects,
  UpdateMonthlyCost,
  UpdateProject,
  UpdateRole,
} from "~/interface/project";
import type { User } from "~/interface/user";

export class ProjectApi {
  private getRequestInfo(method: string, requestBody?: object): RequestInit {
    return {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: true,
      ...{
        body: JSON.stringify(requestBody),
      },
    };
  }

  private getRequestBody(baseBody: object, extraBody?: object): object {
    // ベースとなる共通データと、メソッドごとの追加データをマージ
    return { ...baseBody, ...extraBody };
  }

  async get(organization_id: string, project_id: string): Promise<Project | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/projects/${project_id}`);

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const project = await response.json();
        return project;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async create(organization_id: string, projectData: CreateProject): Promise<Project | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/projects`);

    try {
      const requestInfo = this.getRequestInfo("POST", projectData);

      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(endpoint, requestBody);
      if (response.ok) {
        const project = await response.json();
        return project;
      } else {
        const errorResponse = await response.json();
        console.error(errorResponse.message);
        toast.error(errorResponse.message);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // Fix
  async update(
    organization_id: string,
    project_id: string,
    updateData: UpdateProject,
  ): Promise<Response | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/projects/${project_id}`);
    const requestInfo = this.getRequestInfo("POST", updateData);
    const requestBody = this.getRequestBody(requestInfo);

    try {
      const response = await fetch(endpoint, requestBody);
      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error("errorMessage", errorMessage);
        return null;
      }
    } catch (error) {
      console.error("error", error);
      return null;
    }
  }

  async updateMonthlyCost(
    organization_id: string,
    id: string,
    monthlyCosts: UpdateMonthlyCost,
  ): Promise<Response | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${id}/monthly-cost`,
    );
    const requestInfo = this.getRequestInfo("POST", monthlyCosts);
    const requestBody = this.getRequestBody(requestInfo);

    try {
      const response = await fetch(endpoint, requestBody);
      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error("errorMessage", errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async delete(organization_id: string, project_id: string): Promise<Response | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/projects/${project_id}`);

    try {
      const requestInfo = this.getRequestInfo("DELETE");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error("errorMessage", errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  // project user
  async getAllProjects(
    organization_id: string,
  ): Promise<{ projects: Projects[]; totalCount: number } | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/users/me/projects`);

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const projectsResult = await response.json();
        const project = projectsResult.projects;
        const totalCount = projectsResult.total_count;
        return {
          projects: project,
          totalCount: totalCount,
        };
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getMembers(id: string, organization_id: string): Promise<ProjectMember[] | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/projects/${id}/users`);

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const users = await response.json();
        return users;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getInvitableUser(organization_id: string, project_id: string): Promise<User[] | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/users/invitable`,
    );

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const users = await response.json();
        return users;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async invite(
    organization_id: string,
    project_id: string,
    inviteData: InviteProjectBody,
  ): Promise<Response | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/users`,
    );

    try {
      const requestInfo = this.getRequestInfo("POST", inviteData);
      const requestBody = this.getRequestBody(requestInfo);

      const response = await fetch(endpoint, requestBody);

      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async updateRole(
    organization_id: string,
    id: string,
    userId: string,
    updateRole: UpdateRole,
  ): Promise<boolean> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${id}/users/${userId}`,
    );
    const requestInfo = this.getRequestInfo("POST", updateRole);
    const requestBody = this.getRequestBody(requestInfo);

    try {
      const response = await fetch(endpoint, requestBody);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error("errorMessage", errorMessage);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async removeUser(
    organization_id: string,
    project_id: string,
    target_user_id: string,
  ): Promise<boolean> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/users/${target_user_id}`,
    );
    const requestInfo = this.getRequestInfo("DELETE");
    const requestBody = this.getRequestBody(requestInfo);

    try {
      const response = await fetch(endpoint, requestBody);
      if (response.ok) {
        return true;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error("errorMessage", errorMessage);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async getProjectCount(organization_id: string): Promise<{ count: number; max: number } | null> {
    const endpoint = generateEndpoint(`/organizations/${organization_id}/projects?count=true`);

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }
}
